import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@fuxi/eevee-icon';
import { Skeleton, Tooltip } from 'antd';
import { useMount } from 'ahooks';
import { DisclaimerDialog, Header, Toast, When } from '@/components';
import { selectedDeviceUuid, selectedStoreInfo } from '@/store/device';
import { ALL_PAGES } from '@/constant';
import { useDevice, useNav, useWeeklyPopup } from '@/hooks';
import service from '@/service';
import { DisclaimerDialogRefAttrs } from '@/components/disclaimer-dialog';
import loudouImg from '@/assets/image/loudou.png';
import questionImg from '@/assets/image/question.png';
import directionImg from '@/assets/image/direction.png';
import bookImg from '@/assets/image/book.png';
import QuickGuide, { QuickGuideModalRefAttrs } from './quick-guide';
import EditStoreName from './edit-store-name';

import cx from './index.module.scss';
import { setBannerList, setHelpConfigList, useHelpFeedbackStore } from './store/useHelpFeedbackStore';

interface Props {}

const HelpHomePage: React.FC<Props> = () => {
  const nav = useNav();
  const bannerList = useHelpFeedbackStore.use.bannerList();
  const helpConfigList = useHelpFeedbackStore.use.helpConfigList();
  const deviceUuid = useSelector(selectedDeviceUuid);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const storeInfo = useSelector(selectedStoreInfo);
  const quickGuideRef = useRef<QuickGuideModalRefAttrs>(null);
  const [isIniting, setIsIniting] = useState(true);
  const { checkIsApp } = useDevice();
  const clauseDialogRef = useRef<DisclaimerDialogRefAttrs>(null);

  const currentHaveManageVideo = useMemo(
    () => helpConfigList?.some(item => item.module === 'manage'),
    [helpConfigList]
  );

  const CARD_ITEMS = useMemo(
    () => [
      {
        icon: <img src={loudouImg} alt="" />,
        title: '反馈进度',
        subTitle: '上报问题的处理进度',
        onClick: () => nav(ALL_PAGES.FEEDBACK_PROGRESS),
      },
      {
        icon: <img src={questionImg} alt="" />,
        title: '常见问题',
        subTitle: '使用中的常见疑问&解答',
        onClick: () =>
          nav(ALL_PAGES.IFRAME_CONTENT, {
            state: {
              title: '常见问题',
              iframeUrl: ['prod'].includes(__BUILD_ENV__)
                ? 'https://massage-robot-docs.easefuture.com/issue-guide/robot-body/robot-cannot-start-up.html'
                : 'https://massage-robot-docs-qa.easefuture.com/issue-guide/robot-body/robot-cannot-start-up.html',
            },
          }),
      },
      {
        icon: <img src={bookImg} alt="" />,
        title: '使用说明书',
        subTitle: '详细的机器人使用说明',
        onClick: () =>
          nav(ALL_PAGES.IFRAME_CONTENT, {
            state: {
              title: '使用说明书',
              iframeUrl: ['prod'].includes(__BUILD_ENV__)
                ? 'https://massage-robot-docs.easefuture.com/instructions/basic-desc/cover/cover.html'
                : 'https://massage-robot-docs-qa.easefuture.com/instructions/basic-desc/cover/cover.html',
            },
          }),
      },
      {
        icon: <img src={directionImg} alt="" />,
        title: '快速指南',
        subTitle: '机器人简易操作指南',
        onClick: () => quickGuideRef.current?.show(),
      },
    ],
    [nav]
  );

  const handleNetworkConfig = useCallback(() => {
    if (checkIsApp()) {
      nav(ALL_PAGES.NETWORK_CONFIG);
    } else {
      Toast.show('该功能仅可在PAD上使用');
    }
  }, [checkIsApp, nav]);

  const initData = useCallback(async () => {
    try {
      const { data: helpList } = await service.help.getHelpList();
      setHelpConfigList(helpList);

      const { data } = await service.help.getBannerList();
      setBannerList(data);
    } finally {
      setIsIniting(false);
    }
  }, []);

  useMount(initData);

  return (
    <div className={cx('HelpHomePage')}>
      <Header showBack={false} title="帮助与反馈" />
      <div className={cx('banner')}>
        <Skeleton loading={isIniting} title={false} paragraph={{ rows: 2, width: [200, 160] }}>
          <div
            className={cx('use-skills')}
            onClick={() => nav(ALL_PAGES.USE_SKILLS)}
            style={{ backgroundImage: `url(${bannerList?.find(item => item.module === 'useModeBanner')?.imageUrl})` }}
          >
            <div className={cx('use-skills-box')}>
              <div className={cx('use-skills-title')}>
                <div className={cx('main-title')}>
                  {bannerList?.find(item => item.module === 'useModeBanner')?.title}
                </div>
                <div className={cx('sub-title')}>
                  {bannerList?.find(item => item.module === 'useModeBanner')?.subTitle}
                </div>
              </div>

              <div className={cx('view-skills')}>
                <div className={cx('btn')}>
                  <div className={cx('text')}>查看使用技巧</div>
                  <Icon name="video-play" color="#0055FF" className={cx('icon')} />
                </div>
              </div>
            </div>
          </div>
        </Skeleton>
        <Skeleton loading={isIniting} title={false} paragraph={{ rows: 2, width: [200, 160] }}>
          <div
            className={cx('sku-skills')}
            onClick={() => currentHaveManageVideo && nav(ALL_PAGES.USE_SKILLS, { state: { showSkuSkills: true } })}
            style={{ backgroundImage: `url(${bannerList?.find(item => item.module === 'manageBanner')?.imageUrl})` }}
          >
            <div className={cx('sku-skills-box')}>
              <div className={cx('sku-skills-title')}>
                <div className={cx('main-title')}>
                  {bannerList?.find(item => item.module === 'manageBanner')?.title}
                </div>
                <div className={cx('sub-title')}>
                  <div className={cx('show-manage-videos')}>
                    <div>{bannerList?.find(item => item.module === 'manageBanner')?.subTitle}</div>
                    <When condition={currentHaveManageVideo}>
                      <Icon className={cx('icon')} name="arrow-right" />
                    </When>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Skeleton>
      </div>
      <div className={cx('issue-solve')}>
        <div className={cx('issue-solve-header')}>使用有疑惑，这里有解答</div>
        <div className={cx('issue-solve-box')}>
          {CARD_ITEMS.map(item => (
            <div className={cx('issue-solve-box-item')} key={item.title} onClick={item.onClick}>
              <div className={cx('icon-box')}>{item.icon}</div>
              <div className={cx('title')}>{item.title}</div>
              <div className={cx('sub-title')}>{item.subTitle}</div>
            </div>
          ))}
        </div>
      </div>
      <div className={cx('sub-controls')}>
        <div className={cx('sub-controls-item')} onClick={() => nav(ALL_PAGES.VERSION_UPDATE_NOTICE)}>
          <div>版本更新看这里</div>
          <Icon name="arrow-right" className={cx('icon')} />
        </div>
        <div className={cx('sub-controls-item')} onClick={handleNetworkConfig}>
          <div>为机器人配网</div>
          <Icon name="arrow-right" className={cx('icon')} />
        </div>
        <div
          className={cx('sub-controls-item')}
          onClick={() => {
            clauseDialogRef.current?.show();
          }}
        >
          <div>免责声明</div>
          <Icon name="arrow-right" className={cx('icon')} />
        </div>
      </div>
      <div className={cx('pad-info')}>
        <div className={cx('info-item')}>
          <div className={cx('label')}>设备所属门店</div>
          <div className={cx('info-value')}>
            {storeInfo?.customName && (
              <Tooltip title={`原始门店名称：${storeInfo?.name}`} trigger={['click']}>
                <Icon name="info-circle" size={18} className={cx('tip-icon')} />
              </Tooltip>
            )}
            <div className={cx('store-name')}>{storeInfo.customName || storeInfo.name}</div>
            <Icon name="edit" color="#0055FF" size={20} onClick={() => setIsEditModalOpen(true)} />
          </div>
        </div>
        <div className={cx('info-item')}>
          <div className={cx('label')}>平板信息</div>
          <div className={cx('info-value')}>{deviceUuid}</div>
        </div>
        <div className={cx('info-item')}>
          <div className={cx('label')}>平板版本号</div>
          <div className={cx('info-value')}>V{__APP_VERSION__}</div>
        </div>
        <div className={cx('info-item')}>
          <div className={cx('label')}>版本发布时间</div>
          <div className={cx('info-value')}>
            <Tooltip title={`具体发布时间：${__UPDATE_TIME__}`} trigger={['click']}>
              <Icon size={18} name="info-circle" className={cx('tip-icon')} />
            </Tooltip>
            <div className={cx('update-time')}>{__UPDATE_TIME__?.split(' ')?.[0]}</div>
          </div>
        </div>
      </div>
      <div className={cx('to-feedback')} onClick={() => nav(ALL_PAGES.ISSUE_FEEDBACK)}>
        <Icon name="note" size={28} />
        <div className={cx('feedback-text')}>问题反馈</div>
      </div>
      <EditStoreName open={isEditModalOpen} setOpen={setIsEditModalOpen} />
      <QuickGuide ref={quickGuideRef} />
      <DisclaimerDialog ref={clauseDialogRef} />
    </div>
  );
};

export default HelpHomePage;
